import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  forwardRef,
  Host,
  Provider,
  Renderer2
} from '@angular/core';

import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';

import {
  formatDate,
  getLocale,
  isAfter,
  isBefore,
  isDate,
  isDateValid,
  parseDate,
  // utcAsLocal
} from '../chronos';

import { BsDatepickerDirective } from './pmd-datepicker.component';
import { BsLocaleService } from './pmd-locale.service';

const BS_DATEPICKER_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  /* tslint:disable-next-line: no-use-before-declare */
  useExisting: forwardRef(() => BsDatepickerInputDirective),
  multi: true
};

const BS_DATEPICKER_VALIDATOR: Provider = {
  provide: NG_VALIDATORS,
  /* tslint:disable-next-line: no-use-before-declare */
  useExisting: forwardRef(() => BsDatepickerInputDirective),
  multi: true
};

@Directive({
  selector: `input[bsDatepicker]`,
  host: {
    '(change)': 'onChange($event)',
    '(keyup.esc)': 'hide()',
    '(keydown)': 'onKeydownEvent($event)',
    '(blur)': 'onBlur()'
  },
  providers: [BS_DATEPICKER_VALUE_ACCESSOR, BS_DATEPICKER_VALIDATOR]
})
export class BsDatepickerInputDirective
  implements ControlValueAccessor, Validator {
  private _onChange = Function.prototype;
  private _onTouched = Function.prototype;
  /* tslint:disable-next-line: no-unused-variable */
  private _validatorChange = Function.prototype;
  private _value: Date;

  ClassName = {
    FLOATING_COMPLETE: 'pmd-textfield-floating-label-completed',
    FLOATING_ACTIVE: 'pmd-textfield-floating-label-active'
  };

  constructor(@Host() private _picker: BsDatepickerDirective,
    private _localeService: BsLocaleService,
    private _renderer: Renderer2,
    private _elRef: ElementRef,
    private changeDetection: ChangeDetectorRef) {
    // update input value on datepicker value update
    this._picker.bsValueChange.subscribe((value: Date) => {
      // this._renderer.addClass(this._elRef.nativeElement.parentElement.parentElement, this.ClassName.FLOATING_ACTIVE);
      // this._renderer.addClass(this._elRef.nativeElement.parentElement.parentElement, this.ClassName.FLOATING_COMPLETE);
      this._setInputValue(value);
      if (this._value !== value) {
        this._value = value;
        this._onChange(value);
        this._onTouched();
        if (value !== null) {
          // this._renderer.addClass(this._elementRef.nativeElement.closest(".pmd-textfield"), this.ClassName.FLOATING_ACTIVE);
          // this._renderer.addClass(this._elementRef.nativeElement.closest(".pmd-textfield"), this.ClassName.FLOATING_COMPLETE);
          this._renderer.addClass(this._elRef.nativeElement.parentElement.parentElement, this.ClassName.FLOATING_ACTIVE);
          this._renderer.addClass(this._elRef.nativeElement.parentElement.parentElement, this.ClassName.FLOATING_COMPLETE);
        }
      }
      this.changeDetection.markForCheck();
    });

    // update input value on locale change
    this._localeService.localeChange.subscribe(() => {
      this._setInputValue(this._value);
    });
    // console.log('=======================sdfnsdkjnfsdkf================')
  }

  onKeydownEvent(event: any) {
    // console.log('======onKeydownEvent======', event)
    if (event.keyCode === 13 || event.code === 'Enter') {
      // this.hide();
      this.show();
    }
    // if(event.keyCode === 37 || event.code === 'ArrowLeft') {

    // }
    // if(event.keyCode === 38 || event.code === 'ArrowUp') {

    // }
    // if(event.keyCode === 39 || event.code === 'ArrowRight') {

    // }
    // if(event.keyCode === 40 || event.code === 'ArrowDown') {

    // }
  }

  _setInputValue(value: Date): void {
    // console.log('======_setInputValue======')
    const initialDate = !value ? ''
      : formatDate(value, this._picker._config.dateInputFormat, this._localeService.currentLocale);

    this._renderer.setProperty(this._elRef.nativeElement, 'value', initialDate);
  }

  onChange(event: Event) {
    // console.log('============onChange working=========')
    /* tslint:disable-next-line: no-any*/
    this.writeValue((event.target as any).value);
    this._onChange(this._value);
    if (this._picker._config.returnFocusToInput) {
      this._renderer.selectRootElement(this._elRef.nativeElement).focus();
    }
    this._onTouched();
  }

  validate(c: AbstractControl): ValidationErrors | null {
    // console.log('======validate======')
    const _value: Date | string = c.value;

    /* tslint:disable-next-line: prefer-switch */
    if (_value === null || _value === undefined || _value === '') {
      return null;
    }

    if (isDate(_value)) {
      const _isDateValid = isDateValid(_value);
      if (!_isDateValid) {
        return { bsDate: { invalid: _value } };
      }

      if (this._picker && this._picker.minDate && isBefore(_value, this._picker.minDate, 'date')) {
        this.writeValue(this._picker.minDate);

        return { bsDate: { minDate: this._picker.minDate } };
      }

      if (this._picker && this._picker.maxDate && isAfter(_value, this._picker.maxDate, 'date')) {
        this.writeValue(this._picker.maxDate);

        return { bsDate: { maxDate: this._picker.maxDate } };
      }
    }
  }

  registerOnValidatorChange(fn: () => void): void {
    // console.log('======registerOnValidatorChange======')
    this._validatorChange = fn;
  }

  writeValue(value: Date | string) {
    // console.log('======writeValue======')
    if (!value) {
      this._value = null;
    } else {
      const _localeKey = this._localeService.currentLocale;
      const _locale = getLocale(_localeKey);
      if (!_locale) {
        throw new Error(
          `Locale "${_localeKey}" is not defined, please add it with "defineLocale(...)"`
        );
      }

      this._value = parseDate(value, this._picker._config.dateInputFormat, this._localeService.currentLocale);

      if (this._picker._config.useUtc) {
        // this._value = utcAsLocal(this._value);
      }
    }

    this._picker.bsValue = this._value;
  }

  setDisabledState(isDisabled: boolean): void {
    // console.log('======setDisabledState======')
    this._picker.isDisabled = isDisabled;
    if (isDisabled) {
      this._renderer.setAttribute(this._elRef.nativeElement, 'disabled', 'disabled');

      return;
    }
    this._renderer.removeAttribute(this._elRef.nativeElement, 'disabled');
  }

  registerOnChange(fn: () => void): void {
    // console.log('======registerOnChange======')
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    // console.log('======registerOnTouched======')
    this._onTouched = fn;
  }

  onBlur() {
    // console.log('======onBlur======')
    this._onTouched();
  }

  hide() {
    // console.log('======hide======')
    this._picker.hide();
    this._renderer.selectRootElement(this._elRef.nativeElement).blur();
    if (this._picker._config.returnFocusToInput) {
      this._renderer.selectRootElement(this._elRef.nativeElement).focus();
    }
  }

  show() {
    this._picker.show();
    setTimeout(() => {
      let el = document.getElementById('current');
      el.focus()
    }, 0)
  }
}
